import React from 'react'
import { WallboxModel } from '../../wallboxes'
import { ChargingCardModel } from '../api/models/charging-card-model'
import { ShowLinkedWallboxesButton } from './ShowLinkedWallboxesButton'
import { LinkedWallboxes } from 'src/Libs/e-mobility/charging-cards/components/LinkedWallboxes'
import { ChargingCardLinkedWallboxesChip } from './ChargingCardLinkedWallboxesChip'
import { CardStatus } from '../../shared'

interface LinkedWallboxesCellProps {
  linkedWallboxes: WallboxModel[]
  installationId: number
  chargingCard: ChargingCardModel
  isManagedMode: boolean
}

/**
 * Component that decides what to render inside the "Linked wallboxes" cell of the Charging card table.
 *
 * Since content of this cell can vary in size and show either nothing, single wallbox or eight wallboxes max, we
 * display different content based on the total number of linked wallboxes.
 * If there are no wallboxes linked to any card, we display green chip
 * If there are no wallboxes linked to this charging card, we display red chip.
 * If there is one wallbox linked, we display the wallbox name and id.
 * If there are two wallboxes linked, we display two rows of wallbox identifier.
 * If there are 3+ wallboxes linked, we display "Show linked wallboxes (3+)" button that opens a separate dialog in order
 * not to clutter the table cell with many wallbox names and ids.
 */
export const LinkedWallboxesCell: React.FC<LinkedWallboxesCellProps> = ({ installationId, linkedWallboxes, chargingCard, isManagedMode }) => {
  /** If charging card status is expired, rejected or innactive, we display red chip */
  if (chargingCard.status === CardStatus.expired || chargingCard.status === CardStatus.rejected || chargingCard.status === CardStatus.inactive) {
    return <ChargingCardLinkedWallboxesChip cardUsability="noWallboxes" />
  }

  /** If there are no wallboxes linked to any card(unmanaged mode), we display green chip */
  if (!isManagedMode) {
    return <ChargingCardLinkedWallboxesChip cardUsability="allWallboxes" />
  }

  /** If there are no wallboxes linked to the charging card, we display red chip */
  if (linkedWallboxes.length === 0) {
    return <ChargingCardLinkedWallboxesChip cardUsability="noWallboxes" />
  }

  /** If there are 1 or 2 wallboxes linked, we display a stack of rows, each row containing the wallbox name + */
  if (linkedWallboxes.length === 1 || linkedWallboxes.length === 2) {
    return <LinkedWallboxes linkedWallboxes={linkedWallboxes} />
  }

  /**
   * If there are 3 or more wallboxes linked, the cell becomes very cluttered so we don't display each wallbox label,
   * but instead a button "Show linked wallboxes (3+)" that will open a separate dialog showing all linked wallboxes.
   */
  return <ShowLinkedWallboxesButton linkedWallboxes={linkedWallboxes} chargingCard={chargingCard} installationId={installationId} />
}
